import { Printer } from 'lucide-react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useActiveFinish, usePlatformLook } from '../../platform.tsx';
import { Button } from '../ui.tsx';

/**
 * Documento impresso (ADR 0128): a folha A4 na tela e no papel. Para o papel, uma cópia da folha
 * fica direto no `body` (`.ui-print-root`, escondida na tela): na impressão só ela aparece, do
 * canto da página e com quantas páginas precisar, longe de onde a moldura pôs a tela. O cabeçalho
 * tem o logo do sistema no jeito do acabamento (`docHeader`: linha ou faixa). Cores do tema.
 */
export function PrintDocument({
  title,
  code,
  issuedAt,
  parties,
  children,
  footer,
  printable = true,
}: {
  /** O tipo do documento ("Orçamento", "Recibo"). */
  title: ReactNode;
  /** O número ("ORC-2026-000123"). */
  code?: ReactNode;
  /** Data de emissão, já formatada. */
  issuedAt?: ReactNode;
  /** Blocos de quem emite e de quem recebe, lado a lado. */
  parties?: ReactNode;
  children: ReactNode;
  /** Observações, assinatura, condições. */
  footer?: ReactNode;
  /** Sem impressão (a prévia do estúdio): só a folha na tela, sem o botão nem a cópia no body. */
  printable?: boolean;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const { docHeader } = useActiveFinish();
  const band = docHeader === 'band';
  const sheet = (
    <article className="ui-print-sheet ui-panel flex min-h-[297mm] w-full max-w-[210mm] flex-col bg-sheet text-ink">
      <header
        className={`flex items-start justify-between gap-4 px-[14mm] ${
          band
            ? 'bg-sidebar py-[8mm] text-sidebar-text'
            : 'border-b-2 border-accent pt-[14mm] pb-[6mm]'
        }`}
      >
        <img
          src={look.logoUrl}
          alt={look.appName}
          className="h-10 w-auto max-w-48 object-contain"
        />
        <div className="flex flex-col items-end gap-0.5 text-right">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {code && <span className="font-mono text-sm">{code}</span>}
          {issuedAt && (
            <span className={`text-xs ${band ? 'text-sidebar-muted' : 'text-muted'}`}>
              {t('patterns.document.issuedAt', { date: issuedAt })}
            </span>
          )}
        </div>
      </header>
      <div className="flex flex-1 flex-col gap-6 px-[14mm] py-[10mm] text-sm">
        {parties && <div className="grid gap-6 sm:grid-cols-2">{parties}</div>}
        {children}
      </div>
      {footer && (
        <footer className="border-t border-rule px-[14mm] py-[8mm] text-xs text-muted">
          {footer}
        </footer>
      )}
    </article>
  );
  return (
    <div className="flex flex-col items-center gap-3">
      {printable && (
        <div className="flex w-full max-w-[210mm] justify-end">
          <Button variant="secondary" onClick={() => window.print()}>
            <Printer size={16} aria-hidden />
            {t('patterns.document.print')}
          </Button>
        </div>
      )}
      {sheet}
      {printable &&
        typeof document !== 'undefined' &&
        createPortal(<div className="ui-print-root">{sheet}</div>, document.body)}
    </div>
  );
}

/** Bloco de quem emite ou recebe o documento: o rótulo pequeno e as linhas. */
export function DocumentParty({ label, lines }: { label: ReactNode; lines: ReactNode[] }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="ui-cap text-xs font-medium text-muted">{label}</span>
      {lines.map((l, i) => (
        <span key={i} className={i === 0 ? 'font-medium' : ''}>
          {l}
        </span>
      ))}
    </div>
  );
}
